import { Colors } from 'baseStyles/enums/colors';
import React, { useState } from 'react';
import FlexBox from 'schoolUiComponents/basic/Box/FlexBox';
import { FlexProps } from 'schoolUiComponents/basic/Box/FlexBox/types';
import Button from 'schoolUiComponents/basic/Button';
import Input from 'schoolUiComponents/basic/Input';
import { InputTypes } from 'schoolUiComponents/basic/Input/types';
import Text from 'schoolUiComponents/basic/Text';
import { IErrorStates, IS_EU_USER, useTeacherSignupState } from '../../state';
import TeacherSignupStyles from '../../style';
import { validateDobYear, validateEmailRegex, validateMinLength } from '../../utils';
import { sendErrorToSentry } from 'ErrorBoundary/sendErrorToSentry';
import useSignupCookie from 'hooks/useSignupCookie';

const MESSAGE_MAP: any = {
    TOKEN_REQUIRED: "Signup failed. Please reload the page or contact support.",
    TOKEN_INVALID: "Signup failed. Please reload the page or contact support.",
}

const Form = () => {
    const themeStyles = TeacherSignupStyles();
    const { makeTeacherSignup, toggleTnCPopup, firstName, lastName, schoolEmail, password, yearOfBirth, errorStates, setFirstName, setLastName, setSchoolEmail, setPassword, setYearOfBirth, setErrorStates } = useTeacherSignupState();
    const [isEmailExist, setEmailExist] = useState(false);
    const { setCookie } = useSignupCookie();
    const checkEmailExist = async () => {
        if (schoolEmail?.length && validateEmailRegex(schoolEmail)) {
            try {
                const response = await fetch("/api/v3/email_exists", {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ email: schoolEmail }),
                });
                const data = await response.json();
                if (data.response) {
                    setEmailExist(true);
                }
            } catch (e) {
                sendErrorToSentry(e, { methodName: "checkEmailExist" });
            }
        }
    }

    const validateInputs = async (e: any) => {
        e?.stopPropagation();
        e?.preventDefault();
        
        let validInputs = true;

        if (firstName?.length <= 0) {
            validInputs = false;
            setErrorStates((errorStates: IErrorStates) => ({ ...errorStates, firstName: true }));
        }

        if (lastName?.length <= 0) {
            validInputs = false;
            setErrorStates((errorStates: IErrorStates) => ({ ...errorStates, lastName: true }));
        }

        if (!validateEmailRegex(schoolEmail)) {
            validInputs = false;
            setErrorStates((errorStates: IErrorStates) => ({ ...errorStates, schoolEmail: true }));
        }

        if (!validateMinLength(password, 6)) {
            validInputs = false;
            setErrorStates((errorStates: IErrorStates) => ({ ...errorStates, password: true }));
        }

        if (!IS_EU_USER) {
            const dobRes = validateDobYear(yearOfBirth);
            if (dobRes) {
                validInputs = false;
                setErrorStates((errorStates: IErrorStates) => ({ ...errorStates, yearOfBirth: dobRes }));
            }
        }

        if (validInputs && !isEmailExist) {
            if (IS_EU_USER) {
                toggleTnCPopup(true);
            } else {
                makeTeacherSignup()
            }
        }

        if (validInputs && !isEmailExist) {
            // Setting cookie with signup
            setCookie('userSignedUp', 'true', {
                path: '/',
                secure: true,
                sameSite: 'Strict'
            });
        } 
    }

    return (
        <form style={{width: '100%'}} autoComplete='off' onSubmit={validateInputs}>
            <FlexBox fullWidth alignStart column gap={24}>
                <FlexBox fullWidth justifyContent={FlexProps.JustifyContent.SPACE_BETWEEN} alignItems={FlexProps.AlignItems.FLEX_START} {...themeStyles.signupFormInputBlocks}>
                    <FlexBox flex={1} {...themeStyles.signupFormInputs} margin={themeStyles.signupFirstDivInputs.margin}>
                        <Input
                            width={`100%`}
                            value={firstName}
                            placeholder={`First Name`}
                            autoFocus={true}
                            tabIndex={1}
                            onChange={(e) => {
                                setErrorStates((errorStates: IErrorStates) => ({ ...errorStates, firstName: false }));
                                setFirstName(e?.target?.value)
                            }}
                            name="sl-user-first-name"
                            color={!firstName?.length ? Colors.CONTENT_TEXT_TERTIARY : Colors.DARK_GREY}
                            hasError={errorStates.firstName}
                            errorMessage={errorStates?.firstName ? `Can't be blank` : null}
                            padding={"16px 24px"}
                            customPlaceholder
                            fontSize={16}
                            lineHeight={24}
                            onClick={() => { }}
                            data-test-id="user-first-name"
                            aria-required="true"
                            id="user_first_name"
                            autocomplete="given-name"
                        />
                    </FlexBox>

                    <FlexBox flex={1} {...themeStyles.signupFormInputs}>
                        <Input
                            width={`100%`}
                            value={lastName}
                            placeholder={`Last Name`}
                            tabIndex={1}
                            onChange={(e) => {
                                setErrorStates((errorStates: IErrorStates) => ({ ...errorStates, lastName: false }));
                                setLastName(e?.target?.value)
                            }}
                            name="sl-user-last-name"
                            color={!lastName?.length ? Colors.CONTENT_TEXT_TERTIARY : Colors.DARK_GREY}
                            hasError={errorStates?.lastName}
                            errorMessage={errorStates?.lastName ? `Can't be blank` : null}
                            padding={"16px 24px"}
                            customPlaceholder
                            fontSize={16}
                            lineHeight={24}
                            onClick={() => { }}
                            data-test-id="user-last-name"
                            aria-required="true"
                            id="user_last_name"
                            autocomplete="family-name"
                        />
                    </FlexBox>
                </FlexBox>

                <Input
                    width={`100%`}
                    value={schoolEmail}
                    placeholder={`School Email`}
                    tabIndex={1}
                    onChange={(e) => {
                        setErrorStates((errorStates: IErrorStates) => ({ ...errorStates, schoolEmail: false }));
                        setSchoolEmail(e?.target?.value);
                        setEmailExist(false);
                    }}
                    name="sl-user-school-email"
                    color={!schoolEmail?.length ? Colors.CONTENT_TEXT_TERTIARY : Colors.DARK_GREY}
                    hasError={errorStates?.schoolEmail}
                    errorMessage={errorStates?.schoolEmail ? (!schoolEmail?.length ? `Can't be blank` : `Email is invalid`) : null}
                    padding={"16px 24px"}
                    customPlaceholder
                    fontSize={16}
                    lineHeight={24}
                    onClick={() => { }}
                    data-test-id="user-email"
                    aria-required="true"
                    id="user_email"
                    autocomplete="email"
                    onBlur={checkEmailExist}
                >
                    {isEmailExist && (
                        <FlexBox margin={`4px 0 0 10px`}>
                            <Text.FOOTER letterSpacing={0.4} color={Colors.TEXT_ERROR}>User with this email already exists. </Text.FOOTER>
                            <Text.FOOTER noUnderline emphasis onClick={() => window.open("/signin#/teacher?userType=teacher", "_system")} letterSpacing={0.4} cta>Click here to login.</Text.FOOTER>
                        </FlexBox>
                    )}
                </Input>

                <FlexBox justifyContent={IS_EU_USER ? FlexProps.JustifyContent.FLEX_START : FlexProps.JustifyContent.SPACE_BETWEEN} alignItems={FlexProps.AlignItems.FLEX_START} width={IS_EU_USER ? `50%` : `100%`} {...themeStyles.signupFormInputBlocks}>
                    <FlexBox flex={1} {...themeStyles.signupFormInputs} margin={themeStyles.signupFirstDivInputs.margin}>
                        <Input
                            width={`100%`}
                            value={password}
                            placeholder={`Password`}
                            type={InputTypes.PASSWORD}
                            tabIndex={1}
                            onChange={(e) => {
                                setErrorStates((errorStates: IErrorStates) => ({ ...errorStates, password: false }));
                                setPassword(e?.target?.value)
                            }}
                            name="sl-user-password"
                            color={!password?.length ? Colors.CONTENT_TEXT_TERTIARY : Colors.DARK_GREY}
                            hasError={errorStates?.password}
                            errorMessage={errorStates?.password ? (!password?.length ? `Can't be blank` : `Too short, should be atleast 6 characters long`) : null}
                            padding={"16px 24px"}
                            customPlaceholder
                            fontSize={16}
                            lineHeight={24}
                            onClick={() => { }}
                            data-test-id="user-password"
                            aria-required="true"
                            id="user_password"
                        />
                    </FlexBox>

                    {!IS_EU_USER ? <FlexBox flex={1} {...themeStyles.signupFormInputs}>
                        <Input
                            width={`100%`}
                            value={yearOfBirth}
                            placeholder={`Your year of birth`}
                            tabIndex={1}
                            onChange={(e) => {
                                setErrorStates((errorStates: IErrorStates) => ({ ...errorStates, yearOfBirth: false }));
                                setYearOfBirth(e?.target?.value)
                            }}
                            name="sl-user-last-name"
                            color={!yearOfBirth?.length ? Colors.CONTENT_TEXT_TERTIARY : Colors.DARK_GREY}
                            hasError={!!errorStates?.yearOfBirth}
                            errorMessage={errorStates.yearOfBirth ? (!yearOfBirth?.length ? `Can't be blank` : '' + errorStates?.yearOfBirth) : null}
                            padding={"16px 24px"}
                            customPlaceholder
                            fontSize={16}
                            lineHeight={24}
                            onClick={() => { }}
                            data-test-id="user-dob-year"
                            aria-required="true"
                            id="user_dob_year"
                            autocomplete='bday-year'
                        />
                    </FlexBox> : null}
                </FlexBox>

                {errorStates.common?.length > 0 ? <FlexBox margin={`4px 0 0 10px`}><Text.FOOTER letterSpacing={0.4} color={Colors.TEXT_ERROR}>{MESSAGE_MAP[errorStates.common] || errorStates.common}</Text.FOOTER></FlexBox> : null}

                <FlexBox fullWidth className='simple-input-box'>
                    <Button
                        primary
                        height="58px"
                        onClick={validateInputs}
                        dataTestId="sign-up-btn"
                        name="commit"
                        id="submit_1"
                        disabled={isEmailExist}
                    >
                        <Text.H4 color={Colors.WHITE} width={314}>
                            Sign up
                        </Text.H4>
                    </Button>
                </FlexBox>
            </FlexBox>
        </form>
    )
}

export default Form